import { LineChart, StackedAreaChart } from "@get-dx/d3-charts";
import {
  getQueryStringParams,
  setQueryStringParam,
  deleteQueryStringParam,
  objectToQueryString,
  generatePreviousComparisonValues,
  sortFunc,
  snapshotSquadsFromCache,
} from "../../shared/utils";
import { chartTooltipHtml } from "../chartTooltipHtml";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import { peopleFiltersMixin } from "../mixins/peopleFilters";
import { tooltipIfTruncated } from "../../lib/utils";

export function snapshotCsat(defaults) {
  const {
    baseUrl,
    commentsSearchUrl,
    drilldownTabs,
    minResponseThreshold,
    csatRatingOptions,
    tagOptions,
    groups,
    snapshotId,
    commentsEnabled,
    commentsCsvUrl,
  } = defaults;

  const drilldownTab = getQueryStringParams().get("ddt");
  // Ensure valid drilldown tab is set
  if (drilldownTab && !drilldownTabs.some((t) => t.tab == drilldownTab)) {
    setQueryStringParam("ddt", "overview");
  }

  return {
    // Shared tree expansion behavior for team breakdown
    ...teamTreeExpansionMixin(),
    // Shared people filtering behavior
    ...peopleFiltersMixin(),
    // Configure id mapping for CSAT rows and cached squads
    rowIdKey: "snapshot_squad_id",
    cachedIdKey: "id",
    cachedParentKey: "parent_id",
    drilldownTabs,
    groups,
    drilldownData: {},
    drilldownModalOpen: false,
    selectedCsat: null,
    trends: null,
    xStart: null,
    maxCsatScore: null,
    tableDataLoaded: false,
    isFetchingTableData: false,
    tabDataLoaded: false,
    selectedDrilldownTab: getQueryStringParams().get("ddt"),
    csatScores: [],
    csatRatingOptions,
    commentsCsvUrl,
    resultItemsLoaded: false,
    xEnd: null,
    squadNamesFromSquadIds: [],
    showTeamBreakdown: true,
    commentSearchTerm: "",
    commentSearchResults: [],
    searchCommentsLoaded: false,
    itemSearchTerm: "",
    init() {
      this.$root.addEventListener("dx:squadsTree:init", () => {
        this.determineSquadNamesFromSquadIds();

        // Loading directly to modal from URL cached squads are empty, so wait for event
        if (this.squadIds.length != 1) {
          this.showTeamBreakdown = true;
        } else {
          this.showTeamBreakdown = this.cachedSquads.find(
            (squad) => squad.id == this.squadIds[0],
          )?.is_parent;
        }
      });

      if (this.selectedDrilldownTab) {
        const selectedItem = drilldownTabs.find(
          (t) => t.tab == this.selectedDrilldownTab,
        );
        this.fetchTab(this.selectedDrilldownTab, selectedItem.url);
      }

      this.fetchAll();

      this.$watch("$store.snapshotResultFilters.previousSnapshotId", () => {
        this.handleComparisonChange();
      });
    },
    get cachedSquads() {
      return snapshotSquadsFromCache(snapshotId);
    },
    /**
     * Required by teamTreeExpansionMixin - provides cached squad data for parent/child lookups
     * and team hierarchy navigation. Do not remove this method.
     * @returns {Array} Array of squad objects with id, parent_id, is_parent properties
     */
    getCachedSquads() {
      return this.cachedSquads || [];
    },
    determineSquadNamesFromSquadIds() {
      const selectedSquads = this.cachedSquads.filter(
        (squad) =>
          this.squadIds.includes(String(squad.id)) ||
          this.squadIds.includes(squad.id),
      );

      this.squadNamesFromSquadIds = selectedSquads.map((squad) => squad.name);
      // Loading directly to modal from URL cached squads are empty, so wait for event
      if (this.squadIds.length != 1) {
        this.showTeamBreakdown = true;
      } else {
        this.showTeamBreakdown = this.cachedSquads.find(
          (squad) => squad.id == this.squadIds[0],
        )?.is_parent;
      }
    },
    get tagNamesFromTagIds() {
      const selectedTags = tagOptions.filter(
        (tag) =>
          this.tagIds.includes(String(tag.id)) || this.tagIds.includes(tag.id),
      );

      return selectedTags.map((tag) => tag.name);
    },
    get compareTo() {
      // We don't have benchmarks to compare to, it will always be vs previous
      return "vs_prev";
    },
    get csatItems() {
      let items = this.csatScores;

      if (this.itemSearchTerm) {
        const normalizedTearm = this.itemSearchTerm.trim().toLowerCase();
        items = items.filter((item) => {
          return (
            item.name.toLowerCase().includes(normalizedTearm) ||
            item.group?.toLowerCase()?.includes(normalizedTearm)
          );
        });
      }

      items = items.sort(
        sortFunc(this.sortBy, this.compareTo, { reverse: this.sortReverse }),
      );

      const fieldsWithReversibleSortFunctions = [
        "score",
        "calculatedComparison",
      ];

      if (
        this.sortReverse &&
        !fieldsWithReversibleSortFunctions.includes(this.sortBy)
      ) {
        return items.reverse();
      }

      return items;
    },
    get selectedCsatComparison() {
      const rowData = this.selectedCsat;

      if (!rowData) return null;

      return rowData[this.compareTo];
    },
    get hasMultipleTrendValues() {
      return this?.trends?.values?.length > 1;
    },
    get visibleDrilldownTabs() {
      let filteredTabs = drilldownTabs.filter((drilldownTab) => {
        if (drilldownTab.tab == "team_breakdown") {
          return this.showTeamBreakdown && !this.isFilteringByGroups();
        }

        if (drilldownTab.tab == "comments") {
          return commentsEnabled;
        }

        return true;
      });

      return filteredTabs;
    },
    get filtersApplied() {
      return (
        this.$store.snapshotResultFilters.tagIds.length ||
        this.$store.snapshotResultFilters.squadIds.length
      );
    },
    itemPercentage(csatItem, index, withPercent = false) {
      if (!csatItem) return 0;
      const nonNaResponseCount = csatItem.count - csatItem.idk_count;

      if (nonNaResponseCount == 0) return 0;

      const percentage = Math.round(
        (100.0 * csatItem[csatRatingOptions[index].sql_label]) /
          nonNaResponseCount,
      );

      return withPercent ? `${percentage}%` : percentage;
    },
    calculateItemComparison(item) {
      if (!item.previous_score) return null;

      return Math.round(item.score - item.previous_score);
    },
    itemScore(csatItem) {
      if (!csatItem.score) return "NA";

      return Math.round(csatItem.score);
    },
    naPercentageDescription(csatItem) {
      if (!csatItem) return;
      const naPercentage = Math.round(
        (100.0 * csatItem.idk_count) / csatItem.count,
      );

      return `${naPercentage}% of people responded Not applicable`;
    },
    get tagIds() {
      return this.$store.snapshotResultFilters.tagIds;
    },
    get squadIds() {
      const storeValue = this.$store?.snapshotResultFilters?.squadIds;
      const urlValue =
        getQueryStringParams()
          .get("squad_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    get groupIds() {
      const storeValue = this.$store?.snapshotResultFilters?.groupIds;
      const urlValue =
        getQueryStringParams()
          .get("group_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    get sortReverse() {
      return this.$store.snapshotResultFilters.sortReverse;
    },
    get sortBy() {
      return this.$store.snapshotResultFilters.sortBy;
    },
    get drilldownSortBy() {
      return this.$store.snapshotResultFilters.drilldownSortBy;
    },
    get drilldownSortReverse() {
      return this.$store.snapshotResultFilters.drilldownSortReverse;
    },
    get showOrgComparison() {
      return this.$store.snapshotResultFilters.squadIds.length > 0;
    },
    openDrilldownModal(item, targetTab) {
      // Don't show any modals if not enough responses
      if (this.count < minResponseThreshold) return;

      this.drilldownModalOpen = true;
      this.selectedCsat = item;

      setQueryStringParam("csat_id", item.id);

      // Check if targetTab is available in visibleDrilldownTabs, fallback to overview
      if (targetTab) {
        const isTabAvailable = this.visibleDrilldownTabs.some(
          (tab) => tab.tab === targetTab,
        );
        if (!isTabAvailable) {
          targetTab = "overview";
        }
      }

      // Use targetTab if provided, otherwise check URL, otherwise use existing logic
      if (targetTab) {
        this.selectedDrilldownTab = targetTab;
        setQueryStringParam("ddt", targetTab);
      } else if (this.selectedDrilldownTab) {
        // Keep existing tab from URL (for page reloads)
        setQueryStringParam("ddt", this.selectedDrilldownTab);
      } else {
        this.selectedDrilldownTab = "overview";
        setQueryStringParam("ddt", "overview");
      }

      const selectedItem =
        this.drilldownTabs.find((t) => t.tab == this.selectedDrilldownTab) ||
        this.drilldownTabs[0];
      this.fetchTab(selectedItem.tab, selectedItem.url);
    },
    hasIdk(item) {
      return item?.idk_count > 0;
    },
    hasAllIdkResponses(item) {
      return item?.idk_count == item?.count;
    },
    hideCsatDetails() {
      this.drilldownModalOpen = false;
      this.selectedDrilldownTab = null;
      this.drilldownData = {};
      this.trends = null;
      this.selectedCsat = null;

      deleteQueryStringParam("ddt");
      deleteQueryStringParam("csat_id");
    },
    percentageOfResponsesOverview(positionCount, totalFieldCount) {
      const item = this.selectedCsat;

      if (!item) return;

      if (item[totalFieldCount] === 0) return "0%";

      return `${Math.round(
        (100 * item[positionCount]) / item[totalFieldCount],
      )}%`;
    },
    fetchTab(tab, url) {
      if (!this.selectedCsat?.id) return;
      if (tab === "team_breakdown") {
        this.resetTreeState();
        this.isFetchingTableData = true;
      }

      // Make this smarter so if we've already fetched the tab for the SAME internal_tool we don't fetch it again
      this.selectedDrilldownTab = tab;
      setQueryStringParam("ddt", tab);

      return fetch(`${url}${this.queryParams()}`)
        .then((resp) => resp.json())
        .then((data) => {
          this.drilldownData[tab] = { data };
          if (tab === "team_breakdown") {
            this.tableDataLoaded = true;
            this.isFetchingTableData = false;
          }

          this.tabDataLoaded = true;
          this.renderTabDetails(tab, data);
        })
        .catch((error) => {
          console.error("Error fetching tab data:", error);
        });
    },
    queryParams() {
      const paramObj = {
        squad_ids: `${this.squadIds.join(",")}`,
        group_ids: `${this.groupIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        csat_id: this.selectedCsat?.id,
        branch: this.$store.snapshotResultFilters.branch,
        prev_ss: this.previousSnapshotId,
        aggregation:
          this.$store.snapshotResultFilters.aggregation ||
          getQueryStringParams().get("aggregation"),
      };

      return `?${objectToQueryString(paramObj)}`;
    },
    get previousSnapshotId() {
      return this.$store.snapshotResultFilters.previousSnapshotId;
    },
    fetchAll() {
      return this.fetchResultItems();
    },
    fetchResultItems() {
      this.resultItemsLoaded = false;

      let url = `${baseUrl}${this.queryParams()}`;

      return fetch(url)
        .then((resp) => resp.json())
        .then((data) => {
          this.csatScores = data.csat_scores;

          const targetedCsatId = getQueryStringParams().get("csat_id");
          this.selectedCsat = targetedCsatId
            ? this.csatScores?.find((score) => score.id == targetedCsatId)
            : null;
          if (this.selectedCsat) {
            this.openDrilldownModal(this.selectedCsat);
          }

          this.resultItemsLoaded = true;
        });
    },
    renderTabDetails(tab, data) {
      if (tab == "overview") {
        // We get all of the outcome trends in one query so we can reduce queries in the future
        this.trends = data.trends.find(
          (trend) => trend.internal_tool.id == this.selectedCsat?.id,
        ) || { values: [] };
        this.xStart = this.trends.values[0]?.date;
        this.renderCsatTrendChart();
        this.renderCsatDistributionChart();
      }
      if (tab == "team_breakdown") {
        this.calculateMaxTeamScore();
      }
    },
    calculateMaxTeamScore() {
      const arr = this.breakdownItems.map((i) => i.score);

      this.maxCsatScore = Math.max(...arr);
    },
    percentageOfResponses(item, positionCount) {
      if (!item?.count) return "0%";

      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
    tooltipIfTruncated,
    renderCsatTrendChart() {
      const elChart = document.getElementById(`csat-trends-chart`);
      if (!elChart || !this.trends.values?.length) return;

      elChart.innerHTML = null;
      const minVal = Math.min(...this.trends.values.map((v) => v.value));
      const maxVal = Math.max(...this.trends.values.map((v) => v.value));

      const trendSize = this.trends.values.length;
      this.xEnd = this.trends.values[trendSize - 1]?.label;
      // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly
      const heading = this.trends.internal_tool.name;

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate: this.trends.values[trendSize - 1]?.date,
        tooltipHtml(d, cd) {
          const params = {
            heading: heading,
            d: {
              label: d.label,
              value: d.value,
              formattedValue: d.value,
            },
          };

          if (cd) {
            params.cd = {
              label: cd.label,
              value: cd.value,
              formattedValue: cd.value,
            };
          }

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: this.trends.values,
      };

      if (this.trends.values.length > 1) {
        attrs.comparisonValues = generatePreviousComparisonValues(this.trends);
      }

      return new LineChart(attrs);
    },
    xStartLabel() {
      const date = new Date(this.xStart);
      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear();

      return `${month} ${year}`;
    },
    renderCsatDistributionChart() {
      if (!this.selectedCsat || !this.trends || !this.hasMultipleTrendValues)
        return;
      let elChart = document.getElementById(`csat-distribution-chart`);
      if (!elChart) return;

      const csat = this.selectedCsat;
      const trendData = this.trends;
      elChart.innerHTML = null;

      const minVal = Math.min(
        ...trendData.values.map((v) => v.value).concat(csat.benchmark_50),
      );
      const maxVal = Math.max(
        ...trendData.values.map((v) => v.value).concat(csat.benchmark_90),
      );
      const xEnd = trendData.values[trendData.values.length - 1].date;

      // NOTE: reversing here so that the stacks matches the coloring of other distribution charts
      const colors = [
        "#FBCFE8",
        "#FECACA",
        "#FED7AA",
        "#FEF08A",
        "#D9F99D",
        "#BBF7D0",
      ];

      let stackedAreaValues = {
        series: [
          {
            key: "very_dissatisfied",
            name: "Very dissatisfied",
            color: colors[0],
            counts: [],
          },
          {
            key: "dissatisfied",
            name: "Dissatisfied",
            color: colors[1],
            counts: [],
          },
          {
            key: "neutral",
            name: "Neutral",
            color: colors[2],
            counts: [],
          },
          {
            key: "satisfied",
            name: "Satisfied",
            color: colors[3],
            counts: [],
          },
          {
            key: "very_satisfied",
            name: "Very satisfied",
            color: colors[4],
            counts: [],
          },
        ],
        dates: [],
      };

      // NOTE: reversing here so that the stacks matches the coloring of other distribution charts
      stackedAreaValues.series = stackedAreaValues.series.reverse();

      // Formatting the trend data to match the required format the stacked area chart needs
      this.trends.values.forEach((val) => {
        stackedAreaValues.dates.push(val.date);

        stackedAreaValues.series.forEach((breakdownValue) => {
          breakdownValue.counts.push(val.breakdown[breakdownValue.key]);
        });
      });

      // NOTE: reversing here so that the stacks matches the order of the distribution chart above
      stackedAreaValues.series = stackedAreaValues.series.reverse();

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate: xEnd,
        tooltipHtml(d, cd) {
          const params = {
            heading: new Intl.DateTimeFormat("en-US", {
              month: "short",
              year: "numeric",
            }).format(d),
            rows: cd
              .map((r) => ({
                label: r.name,
                value: r.count,
                formattedValue: Math.round(r.percentage * 100) + "%",
                color: r.color,
              }))
              .reverse(),
          };

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: stackedAreaValues,
      };

      return new StackedAreaChart(attrs);
    },
    isSortOrder(order) {
      return this.sortBy == order;
    },
    get breakdownItems() {
      return this.getBreakdownItems({
        sortBy: this.drilldownSortBy,
        compareTo: this.compareTo,
        reverse: this.drilldownSortReverse,
      });
    },
    isDrilldownSortOrder(order) {
      return this.drilldownSortBy == order;
    },
    changeComparison(compareTo) {
      console.log("changeComparison", compareTo);
      this.$store.snapshotResultFilters.setCompareTo(compareTo);
      this.fetchAll();
    },
    changeSortBy(sortBy) {
      if (this.sortBy == sortBy) {
        this.$store.snapshotResultFilters.sortReverse = !this.sortReverse;

        setQueryStringParam("sr", this.sortReverse);
        return;
      }

      this.$store.snapshotResultFilters.sortBy = sortBy;
      this.$store.snapshotResultFilters.sortReverse = false;

      setQueryStringParam("sb", sortBy);
      setQueryStringParam("sr", false);
    },
    changeDrilldownSortBy(sortBy) {
      if (this.drilldownSortBy == sortBy) {
        this.$store.snapshotResultFilters.drilldownSortReverse =
          !this.drilldownSortReverse;

        setQueryStringParam("ddsr", this.drilldownSortReverse);
        return;
      }

      this.$store.snapshotResultFilters.drilldownSortBy = sortBy;
      this.$store.snapshotResultFilters.drilldownSortReverse = false;

      setQueryStringParam("ddsb", sortBy);
      setQueryStringParam("ddsr", false);
    },
    get drilldownComparsionVal() {
      return this.selectedCsat?.vs_previous !== null &&
        this.selectedCsat?.vs_previous !== undefined
        ? Math.round(this.selectedCsat.vs_previous)
        : null;
    },
    // Note: changeSquadIds and changeGroupIds methods are provided by peopleFiltersMixin

    changeTagIds(tagIds) {
      this.$store.snapshotResultFilters.tagIds = tagIds;
      setQueryStringParam("tag_ids", tagIds);

      this.fetchAll();

      // Only clear tree state and refresh team breakdown if modal is actually open
      if (
        this.selectedDrilldownTab === "team_breakdown" &&
        this.drilldownModalOpen
      ) {
        this.resetTreeState();
        this.refetchTeamBreakdown();
      }
    },
    refetchTeamBreakdown() {
      this.resetTreeState();
      this.isFetchingTableData = true;

      const selectedItem = drilldownTabs.find(
        (t) => t.tab == this.selectedDrilldownTab,
      );

      if (selectedItem) {
        this.fetchTab(this.selectedDrilldownTab, selectedItem.url);
      }
    },
    /**
     * Required by teamTreeExpansionMixin - provides the API endpoint for fetching child team data
     * when expanding team rows in the breakdown table. Do not remove this method.
     * @returns {string} The URL endpoint for child data requests
     */
    getChildDataEndpoint() {
      const teamBreakdownTab = this.drilldownTabs.find(
        (t) => t.tab === "team_breakdown",
      );
      return teamBreakdownTab?.url || "";
    },

    /**
     * Required by teamTreeExpansionMixin - provides query parameters for fetching child team data
     * when expanding team rows. Do not remove this method.
     * @returns {Object} Parameters object containing filters and context for the API request
     */
    getChildDataParams() {
      const currentParams = Object.fromEntries(
        getQueryStringParams().entries(),
      );
      return {
        ...currentParams,
        csat_id: this.selectedCsat?.id,
        branch: this.$store?.snapshotResultFilters?.branch,
        prev_ss: this.$store?.snapshotResultFilters?.previousSnapshotId,
        squad_ids: this.squadIds?.join(","),
        group_ids: this.groupIds?.join(","),
        tag_ids: this.tagIds?.join(","),
      };
    },
    get comments() {
      if (this.commentSearchResults.length > 0)
        return this.commentSearchResults;

      if (!this.drilldownData.comments?.data) return [];

      return this.drilldownData.comments.data;
    },
    get noSearchResults() {
      return (
        this.commentSearchResults.length == 0 &&
        this.commentSearchTerm &&
        this.searchCommentsLoaded
      );
    },
    performCommentSearch() {
      this.searchCommentsLoaded = false;
      const finalTerm = this.commentSearchTerm.trim();

      if (finalTerm.length > 2) {
        const searchParams = {
          term: finalTerm,
        };

        const fullUrl = `${commentsSearchUrl}${this.queryParams()}&${objectToQueryString(searchParams)}`;

        return fetch(fullUrl)
          .then((resp) => resp.json())
          .then((data) => {
            this.commentSearchResults = data;
            this.searchCommentsLoaded = true;
          })
          .catch((error) => {
            console.error("Error fetching items", error);
          });
      } else {
        this.commentSearchResults = [];
      }
    },
    clearSearchTerm() {
      this.commentSearchTerm = "";
      this.commentSearchResults = [];
      this.searchCommentsLoaded = false;
    },
    handleComparisonChange() {
      this.fetchAll();
      const currentTab = this.selectedDrilldownTab;
      const tabConfig = this.visibleDrilldownTabs.find(
        (t) => t.tab === currentTab,
      );

      if (currentTab && tabConfig?.url) {
        this.fetchTab(currentTab, tabConfig.url);
      }
    },
    formatTargetingDisplay(item) {
      const roleMapping = {
        dev: "People who write code (i.e., developers)",
        nondev: "People who don't write code",
        all: "Both people who do and don't write code",
      };

      const formatList = (items) => {
        if (!items) return "";
        const itemsList = items.split(", ");
        if (itemsList.length <= 3) return items;
        return `${itemsList.slice(0, 3).join(", ")} and ${itemsList.length - 3} more`;
      };

      switch (item.target_audience) {
        case "configured": {
          const roleText = roleMapping[item.target_role];
          const teamsText = formatList(item.target_squads);
          const tagsText = formatList(item.target_tags);

          if (!teamsText && !tagsText) {
            return `Dynamic audience<div class="mt-2 text-xs">${roleText}</div><div class="flex flex-col text-xs"><div class="mt-2 text-xs">All teams, all attributes</div></div>`;
          }
          return `Dynamic audience<div class="mt-2 text-xs">${roleText}</div><div class="flex flex-col text-xs"><div class="mt-1 text-xs">${teamsText || "All teams"}</div><div class="text-xs mt-1">${tagsText || "All attributes"}</div></div>`;
        }
        case "custom": {
          const users = item.target_users?.split(", ") || [];
          const usersList = users.length ? formatList(users.join(", ")) : "";
          return `Custom list${usersList ? ` <div class="mt-1 text-xs">${usersList}</div>` : ""}`;
        }
        case "platformx":
          return "PlatformX";
        default:
          return "";
      }
    },

    // Get group names for tooltips using mixin resolver
    getGroupNames() {
      return this.resolveGroupNames(this.groups, this.getCurrentGroupIds());
    },
  };
}
